import * as THREE from 'three';
import { Node, NodeMaterial } from 'three/webgpu';
import {
  Fn, uniform, vec2, vec3, vec4, float,
  fract, floor, sin, length, min, max, abs, mix, smoothstep, fwidth, Discard,
  positionWorld
} from 'three/tsl';

// Arena top: derivative grid + radial fade + dissolve. Ported from
// ../shaders/grid.{vert,frag} to TSL for WebGPU.
export class GridMaterial extends NodeMaterial {
  readonly uBaseColor = uniform(new THREE.Vector3(0.102, 0.102, 0.165)); // #1a1a2a
  readonly uLineColor = uniform(new THREE.Vector3(0.486, 0.576, 0.769)); // #7c93c4
  readonly uEdgeGlow  = uniform(new THREE.Vector3(1.0, 0.698, 0.376));   // #ffb260
  readonly uRadius    = uniform(1);
  readonly uGridScale = uniform(1.0);
  readonly uDissolve  = uniform(0.0);

  constructor(radius: number) {
    super();
    this.uRadius.value = radius;

    const gridLine = Fn((args: [Node<'vec2'>, Node<'float'>]) => {
      const [p, scale] = args;
      const scaled = p.mul(scale);
      const g = abs(fract(scaled.sub(0.5)).sub(0.5)).div(fwidth(scaled));
      return float(1).sub(min(min(g.x, g.y), 1));
    });

    const hash2 = Fn((args: [Node<'vec2'>]) => {
      const [p] = args;
      return fract(sin(p.dot(vec2(127.1, 311.7))).mul(43758.5453));
    });

    this.colorNode = Fn(() => {
      const p = positionWorld.xz.toVar();
      const r = length(p);
      const edge = smoothstep(this.uRadius, this.uRadius.sub(0.5), r);

      const g1 = gridLine(p, this.uGridScale);
      const g2 = gridLine(p, this.uGridScale.mul(0.25)).mul(0.6);
      const lines = max(g1, g2);

      let col = mix(this.uBaseColor.mul(0.6), this.uBaseColor, edge);
      col = col.add(this.uLineColor.mul(lines).mul(float(0.4).add(edge.mul(0.6))));

      const ringGlow = smoothstep(this.uRadius.sub(0.18), this.uRadius, r)
        .mul(float(1).sub(smoothstep(this.uRadius, this.uRadius.add(0.05), r)));
      col = col.add(this.uEdgeGlow.mul(ringGlow).mul(1.6));

      const n = hash2(floor(p.mul(10)));
      Discard(this.uDissolve.greaterThan(0).and(n.lessThan(this.uDissolve)));

      const burnRim = smoothstep(this.uDissolve.sub(0.06), this.uDissolve, n)
        .mul(float(1).sub(smoothstep(this.uDissolve, this.uDissolve.add(0.04), n)));
      col = col.add(vec3(1.4, 0.8, 0.4).mul(burnRim).mul(1.2));

      return vec4(col, 1);
    })();
  }

  setDissolve(v: number): void { this.uDissolve.value = v; }
}
